import type { BetterFeatureClientPlugin } from "@di-devs/better-feature/client";
import type { member } from "../server/plugin";
import { createMemberAttributePlugin } from "./member-attribute/plugin";
import { createMemberChatPlugin } from "./member-chat/plugin";
import { createMemberCorePlugin } from "./member-core/plugin";
import { createMemberPointPlugin } from "./member-point/plugin";
import { createMemberVisitPlugin } from "./member-visit/plugin";

export const memberClient = () => {
	return {
		id: "member",
		$InferServerPlugin: {} as ReturnType<typeof member>,
		getAtoms: ($fetch) => {
			// Create subfeature plugins
			const corePlugin = createMemberCorePlugin($fetch);
			const attributePlugin = createMemberAttributePlugin($fetch);
			const visitPlugin = createMemberVisitPlugin($fetch);
			const chatPlugin = createMemberChatPlugin($fetch);
			const pointPlugin = createMemberPointPlugin($fetch);

			return {
				// Member core signals
				$browseMembersSignal: corePlugin.signals.browseMembers,
				$listMembersSignal: corePlugin.signals.listMembers,
				$getMemberSignal: corePlugin.signals.getMember,
				$updateMemberSignal: corePlugin.signals.updateMember,
				$deleteMemberSignal: corePlugin.signals.deleteMember,
				$generateMemberCSVSignal: corePlugin.signals.generateMemberCSV,
				// Member attribute signals
				$listMemberAttributesSignal:
					attributePlugin.signals.listMemberAttributes,
				$createMemberAttributeSignal:
					attributePlugin.signals.createMemberAttribute,
				$updateMemberAttributeSignal:
					attributePlugin.signals.updateMemberAttribute,
				$updateMemberAttributeOrderSignal:
					attributePlugin.signals.updateMemberAttributeOrder,
				$deleteMemberAttributeSignal:
					attributePlugin.signals.deleteMemberAttribute,

				// Member point signals
				$updateMemberPointsSignal: pointPlugin.signals.updateMemberPoints,
				$getMemberPointHistorySignal: pointPlugin.signals.getMemberPointHistory,
				$getDailyPointSummarySignal: pointPlugin.signals.getDailyPointSummary,
				$bulkUpdateMemberPointsSignal:
					pointPlugin.signals.bulkUpdateMemberPoints,

				// use hooks for atoms - Better Feature will add "use" prefix automatically

				// Member core queries - Better Feature will add "use" prefix automatically
				browseMembers: corePlugin.querySets.browseMembers.query,
				listMembers: corePlugin.querySets.listMembers.query,
				getMember: corePlugin.querySets.getMember.query,
				updateMember: corePlugin.querySets.updateMember.query,
				deleteMember: corePlugin.querySets.deleteMember.query,
				generateMemberCSV: corePlugin.querySets.generateMemberCSV.query,

				// Member attribute queries - Better Feature will add "use" prefix automatically
				listMemberAttributes:
					attributePlugin.querySets.listMemberAttributes.query,
				createMemberAttribute:
					attributePlugin.querySets.createMemberAttribute.query,
				updateMemberAttribute:
					attributePlugin.querySets.updateMemberAttribute.query,
				updateMemberAttributeOrder:
					attributePlugin.querySets.updateMemberAttributeOrder.query,
				deleteMemberAttribute:
					attributePlugin.querySets.deleteMemberAttribute.query,

				// Member point queries - Better Feature will add "use" prefix automatically
				updateMemberPoints: pointPlugin.querySets.updateMemberPoints.query,
				getMemberPointHistory:
					pointPlugin.querySets.getMemberPointHistory.query,
				getDailyPointSummary: pointPlugin.querySets.getDailyPointSummary.query,
				bulkUpdateMemberPoints:
					pointPlugin.querySets.bulkUpdateMemberPoints.query,

				// State atoms from all plugins
				...corePlugin.atoms,
				...attributePlugin.atoms,
				...visitPlugin.atoms,
				...chatPlugin.atoms,
				...pointPlugin.atoms,
			};
		},
		getActions: ($fetch, $store) => {
			// Create subfeature plugins
			const corePlugin = createMemberCorePlugin($fetch, $store);
			const attributePlugin = createMemberAttributePlugin($fetch, $store);
			const visitPlugin = createMemberVisitPlugin($fetch, $store);
			const chatPlugin = createMemberChatPlugin($fetch, $store);
			const pointPlugin = createMemberPointPlugin($fetch, $store);

			return {
				...corePlugin.actions,
				...attributePlugin.actions,
				...visitPlugin.actions,
				...chatPlugin.actions,
				...pointPlugin.actions,
			};
		},
		atomListeners: [],
	} satisfies BetterFeatureClientPlugin;
};
